// Single source of truth for API documentation content.
// Mirror this in /app/backend/routers/docs.py (API_DOCS dict) when editing.
//
// Endpoint schema:
//   { method, path, summary, auth, body?, response?, notes? }
// `body` and `response` are JSON code samples (strings preserved verbatim).
// `notes` is an array of paragraphs (string[]); used to explain computations,
// relationships between entities, and edge cases.

export const API_DOCS = {
  title: 'API Documentation',
  intro:
    "All Ecowater Cloud endpoints are mounted under the `/api` prefix and require a Bearer JWT in the `Authorization` header unless explicitly noted. Money values are returned as strings (decimal-safe). Date/time values use ISO-8601 UTC. IDs flowing in from the ecobridge.tech middleware are always strings — never cast to ObjectId.",
  sections: [
    {
      heading: 'Authentication',
      body: 'Login with a phone number + password to receive a JWT. Tokens expire after 24 hours. After 5 consecutive failed attempts the account is locked for 15 minutes.',
      endpoints: [
        {
          method: 'POST',
          path: '/api/auth/login',
          summary: 'Authenticate via phone + password.',
          auth: 'Public',
          body: `{
  "username": "255767821881",    // E.164 phone, no '+'
  "password": "secret"
}`,
          response: `{
  "access_token": "eyJhbGciOi...",
  "token_type": "bearer",
  "user": {
    "id": "...",
    "fullname": "Jane Doe",
    "role": "super_admin",
    "entity_id": null,
    "enabled_modules": ["dashboard", "subscribers", "campuses", "..."]
  },
  "must_change_password": false
}`,
          notes: [
            'Rate-limited: 5 failed attempts within 15 minutes → 403 with `account_locked_until` until lockout expires.',
            'A successful login resets the failed-attempt counter.',
            'When `must_change_password: true`, the frontend forces /change-password before any other route.',
          ],
        },
        {
          method: 'GET',
          path: '/api/auth/me',
          summary: 'Return current user profile + module access.',
          auth: 'Any authenticated user',
          response: `{
  "id": "...",
  "fullname": "Jane Doe",
  "username": "255767821881",
  "email": "jane@example.com",
  "role": "entity_admin",
  "entity_id": "19",
  "enabled_modules": ["dashboard", "subscribers", "campuses"]
}`,
          notes: [
            '`enabled_modules` is the per-entity sidebar gate. Super admins ignore this set.',
            'The sidebar component degrades gracefully — if the field is missing entirely it falls back to role-only filtering.',
          ],
        },
        {
          method: 'POST',
          path: '/api/auth/logout',
          summary: 'Server-side log of logout. Token revocation is best-effort.',
          auth: 'Any authenticated user',
          response: `{ "ok": true }`,
        },
        {
          method: 'POST',
          path: '/api/auth/refresh',
          summary: 'Mint a new access_token from the http-only refresh cookie.',
          auth: 'Refresh cookie required',
          response: `{ "access_token": "eyJ..." }`,
        },
      ],
    },
    {
      heading: 'Entities · Campuses · Stores',
      body:
        'Multi-tenancy hierarchy. Entity → Campus → Store. All three are proxied live from ecobridge.tech and cached in-process with a short TTL. Writes invalidate the relevant cache key. Entities can be set to ACTIVE / SUSPENDED.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/entities',
          summary: 'List entities visible to caller. Super admin → all; others → just their own.',
          auth: 'super_admin · entity_admin',
          response: `[
  {
    "id": "19",
    "entity_name": "REFILL ECOWATER LIMITED",
    "entity_code": "UGS00001",
    "country_code": "UG",
    "currency_code": "UGX",
    "status": "ACTIVE",
    "subscription_fee": "10000.00",
    "initial_recharge": "5000.00"
  }
]`,
          notes: ['Cached for 30 seconds. Cache key includes the role + entity_id so super-admin output is not served to other roles.'],
        },
        {
          method: 'POST',
          path: '/api/entities',
          summary: 'Create a new entity (tenant).',
          auth: 'super_admin',
          body: `{
  "entity_name": "Acme Water Co.",
  "country_code": "TZ",
  "currency_code": "TZS",
  "subscription_fee": "10000.00",
  "initial_recharge": "5000.00"
}`,
          response: `{ "id": "21", "entity_code": "TZS00002", "status": "ACTIVE", ... }`,
          notes: ['`entity_code` is auto-derived by the middleware as `<COUNTRY>S<5-digit-seq>`.'],
        },
        {
          method: 'GET',
          path: '/api/entities/next-code',
          summary: 'Suggest the next available entity_code for a given country.',
          auth: 'super_admin',
          response: `{ "next_code": "TZS00003" }`,
          notes: ['Pass `?country_code=TZ`. Returns `<COUNTRY>S<5-digit-seq>` (e.g. `TZS00003`) — used to pre-fill the Create Entity dialog.'],
        },
        {
          method: 'GET',
          path: '/api/entities/{id}',
          summary: 'Fetch a single entity with full profile (address, contacts, defaults).',
          auth: 'super_admin · entity_admin (own entity only)',
          response: `{
  "id": "19",
  "entity_code": "UGS00001",
  "entity_type": "Subsidiary",
  "entity_name": "REFILL ECOWATER LIMITED",
  "country_code": "UG",
  "currency_code": "UGX",
  "timezone": "Africa/Kampala",
  "address_line_1": "Plot 10, Kampala Road",
  "telephone_no": "256700000000",
  "email": "ops@refillecowater.com",
  "default_sub_fee": "10000",
  "ic_card_pid": "...",
  "status": "active",
  "campuses_count": 3,
  "stores_count": 4,
  "users_count": 7,
  "created_at": "2026-05-18 06:00:00",
  "middleware_id": "19"
}`,
          notes: ['Non-super-admins can only fetch their own entity. Returns 403 otherwise.'],
        },
        {
          method: 'PUT',
          path: '/api/entities/{id}',
          summary: 'Update entity profile, status, timezone, contacts, fees.',
          auth: 'super_admin',
          body: `{
  "entity_name": "REFILL ECOWATER LIMITED",
  "entity_type": "Subsidiary",
  "country_code": "UG",
  "currency_code": "UGX",
  "timezone": "Africa/Kampala",
  "address_line_1": "Plot 10, Kampala Road",
  "telephone_no": "256700000000",
  "email": "ops@refillecowater.com",
  "default_sub_fee": "10000",
  "status": "active"      // "active" | "suspended"
}`,
          response: `{ ...full EntityResponse... }`,
          notes: [
            'All fields are optional — only those present are updated (PATCH-style semantics on a PUT).',
            'Writes are forwarded to the middleware `/update_entity`. The local TTL cache is invalidated on success.',
            'Audit action: ENTITY_UPDATED.',
          ],
        },
        {
          method: 'DELETE',
          path: '/api/entities/{id}',
          summary: 'Soft-delete (suspend) an entity.',
          auth: 'super_admin',
          response: `{ "ok": true, "id": "19" }`,
          notes: [
            'Routed through the middleware `/update_entity` with status=SUSPENDED — never a hard delete (preserves historical reporting).',
            'Audit action: ENTITY_DELETED.',
          ],
        },
        {
          method: 'GET',
          path: '/api/campuses',
          summary: 'List campuses scoped to caller entity.',
          auth: 'super_admin · entity_admin · staff',
          response: `[
  {
    "id": "10",
    "campus_name": "Kampala Main",
    "campus_code": "UGC00010",
    "entity_id": "19",
    "subscription_fee": "10000.00",
    "initial_recharge": "5000.00",
    "currency_code": "UGX"
  }
]`,
          notes: ['Cached for 30 seconds. ?entity_id={} narrows further (super admin only).'],
        },
        {
          method: 'POST',
          path: '/api/campuses',
          summary: 'Create a new campus under an entity.',
          auth: 'super_admin · entity_admin',
          body: `{
  "campus_name": "Kampala Main",
  "short_name": "KLA-MAIN",
  "entity_id": "19",
  "physical_addr": "Plot 22, Kampala Rd",
  "address_line1": "P.O. Box 1234",
  "telephone": "256700000000",
  "email": "kla@refillecowater.com",
  "bank_acc_no": "0123456789",
  "bank_acc_name": "Refill Ecowater Ltd",
  "bank_name": "Stanbic",
  "subscription_fee": 10000,
  "initial_recharge": 5000
}`,
          response: `{ "id": "10", "campus_code": "UGC00010", "status": "active", ... }`,
          notes: [
            '`campus_code` is auto-derived by the middleware as `<COUNTRY>C<5-digit-seq>`.',
            'Entity-admins can only create campuses inside their own entity.',
          ],
        },
        {
          method: 'GET',
          path: '/api/campuses/{id}',
          summary: 'Fetch a single campus with full details.',
          auth: 'super_admin · entity_admin · staff (own entity only)',
          response: `{
  "id": "10",
  "campus_code": "UGC00010",
  "campus_name": "Kampala Main",
  "short_name": "KLA-MAIN",
  "entity_id": "19",
  "entity_name": "REFILL ECOWATER LIMITED",
  "physical_addr": "Plot 22, Kampala Rd",
  "telephone": "256700000000",
  "email": "kla@refillecowater.com",
  "subscription_fee": 10000.0,
  "initial_recharge": 5000.0,
  "stores_count": 2,
  "status": "active",
  "middleware_id": "10"
}`,
        },
        {
          method: 'PUT',
          path: '/api/campuses/{id}',
          summary: 'Update a campus (name, address, banking, fees, status).',
          auth: 'super_admin · entity_admin',
          body: `{
  "campus_name": "Kampala Main",
  "short_name": "KLA-MAIN",
  "physical_addr": "Plot 22, Kampala Rd",
  "telephone": "256700000000",
  "email": "kla@refillecowater.com",
  "bank_acc_no": "0123456789",
  "bank_acc_name": "Refill Ecowater Ltd",
  "bank_name": "Stanbic",
  "subscription_fee": 10000,
  "initial_recharge": 5000,
  "status": "active"
}`,
          response: `{ ...full CampusResponse... }`,
          notes: [
            'All fields optional; only those present are updated.',
            'Forwarded to middleware `/update_campus`. Local TTL cache invalidated.',
            'Audit action: CAMPUS_UPDATED.',
          ],
        },
        {
          method: 'DELETE',
          path: '/api/campuses/{id}',
          summary: 'Soft-delete a campus.',
          auth: 'super_admin · entity_admin',
          response: `{ "ok": true, "id": "10" }`,
          notes: ['Soft-deletes via status=SUSPENDED on the middleware. Audit action: CAMPUS_DELETED.'],
        },
        {
          method: 'GET',
          path: '/api/campuses/{id}/analytics',
          summary: 'Aggregated analytics for a single campus.',
          auth: 'Any authenticated user with access to the campus',
          response: `{
  "store_count": 3,
  "subscriber_count": 412,
  "wallet_balance_total": "1240500.00",
  "revenue_30d": "8430000.00",
  "trend": [{ "date": "2026-05-01", "amount": "210000.00" }, ...]
}`,
          notes: [
            'Fan-out: this single endpoint internally calls /get_stores, /get_subscribers, /get_payments in parallel. Sub-second response time is dependent on the TTL cache.',
          ],
        },
        {
          method: 'GET',
          path: '/api/stores',
          summary: 'List stores scoped to caller entity / campus.',
          auth: 'super_admin · entity_admin · staff',
          response: `[
  {
    "id": "5",
    "store_name": "Kampala HQ Pump",
    "store_code": "ST-000005",
    "campus_id": "10",
    "entity_id": "19",
    "utility_water": "1300.00",
    "utility_electricity": "355.00",
    "card_balance": "0.00"
  }
]`,
          notes: [
            '`utility_water` and `utility_electricity` are the per-unit rates referenced when logging meter readings (POST /api/campuses/{id}/utility-bills).',
          ],
        },
        {
          method: 'POST',
          path: '/api/stores',
          summary: 'Create a new store under a campus.',
          auth: 'super_admin · entity_admin',
          body: `{
  "device_id": "UTT2025QXP",
  "store_name": "Kampala HQ Pump",
  "entity_id": "19",
  "campus_id": "10",
  "category": "2 Doors",         // "1 Door" | "2 Doors" | "3 Doors" | "4 Doors"
  "inst_type": "University",     // "University" | "Hospital" | "School" | "Office" | "Public"
  "input_water": "Fresh Water",  // "Fresh Water" | "Tap Water" | "Borehole" | "Rain Water" | "Salty Water"
  "mobile": "",
  "permit_no": "",
  "selling_price": 200,
  "utility_water": 1300,
  "utility_electricity": 355,
  "royalty": 2
}`,
          response: `{ "id": "5", "store_code": "ST-000005", "status": "active", ... }`,
          notes: [
            '`device_id` must be unique across all stores.',
            'Server-managed: `store_code`, `maker_id`, `date_registered`, `date_updated`, `created_at`, `middleware_id`.',
          ],
        },
        {
          method: 'GET',
          path: '/api/stores/{id}',
          summary: 'Fetch a single store with full details (assignment, classification, pricing, utility rates, audit).',
          auth: 'super_admin · entity_admin · staff (own scope)',
          response: `{
  "id": "5",
  "store_no": null,
  "store_code": "ST-000005",
  "device_id": "UTT2025QXP",
  "store_name": "Dummy 1 Store",
  "entity_id": "18",
  "entity_name": "Dummy Entity",
  "campus_id": "10",
  "campus_name": "Dummy Campus",
  "category": "2 Doors",
  "inst_type": "University",
  "input_water": "Fresh Water",
  "mobile": "",
  "permit_no": "",
  "selling_price": 200.0,
  "utility_water": 1300.0,
  "utility_electricity": 355.0,
  "royalty": 2.0,
  "status": "Active",
  "operational_status": "Operating",
  "created_at": "2026-05-18 06:39:53",
  "maker_id": "255756656355",
  "date_registered": "2026-05-18 06:39:53",
  "date_updated": "2026-05-18 06:39:53",
  "middleware_id": "5"
}`,
        },
        {
          method: 'PUT',
          path: '/api/stores/{id}',
          summary: 'Update a store (name, device, campus, classification, pricing, utility rates, status).',
          auth: 'super_admin · entity_admin',
          body: `{
  "store_name": "Kampala HQ Pump",
  "device_id": "UTT2025QXP",
  "campus_id": "10",
  "category": "2 Doors",
  "inst_type": "University",
  "input_water": "Fresh Water",
  "mobile": "",
  "permit_no": "",
  "selling_price": 200,
  "utility_water": 1300,
  "utility_electricity": 355,
  "royalty": 2,
  "operational_status": "Operating",  // "Operating" | "Fault" | "Under Maintenance"
  "status": "Active"                  // "Active" | "Inactive"
}`,
          response: `{ ...full StoreResponse... }`,
          notes: [
            'All fields optional — only those present are updated (PATCH-style semantics on a PUT).',
            '`device_id` is editable but must remain unique across all stores (enforced by the middleware).',
            'Writes are forwarded to middleware `/update_store`. The stores TTL cache is invalidated on success.',
            'Audit action: STORE_UPDATED.',
          ],
        },
        {
          method: 'DELETE',
          path: '/api/stores/{id}',
          summary: 'Soft-delete a store.',
          auth: 'super_admin · entity_admin',
          response: `{ "ok": true, "id": "5" }`,
          notes: ['Soft-deletes via status=SUSPENDED on the middleware. Audit action: STORE_DELETED.'],
        },
        {
          method: 'GET',
          path: '/api/stores/{id}/analytics',
          summary: 'Per-store dashboard data: sales totals, hourly histogram, weekday breakdown.',
          auth: 'Any authenticated user with access to the store',
          response: `{
  "month_revenue": "4200000.00",
  "last_month_revenue": "3800000.00",
  "delta_percent": 10.5,
  "hourly": [{ "hour": 0, "volume": 12 }, ...],
  "weekday": [{ "weekday": "Mon", "volume": 421 }, ...]
}`,
          notes: [
            '`delta_percent = (month_revenue − last_month_revenue) / last_month_revenue × 100`. A 0% delta when last_month_revenue is 0 is returned as `null` (avoids divide-by-zero).',
          ],
        },
      ],
    },
    {
      heading: 'Subscribers · Cards · Cashiers',
      body:
        'Subscribers are end-users with prepaid wallets bound to a card. Cards can be FEE (paid plan) or FREE. Cashiers move money from float into subscriber wallets via the recharge flow.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/subscribers',
          summary: 'List subscribers scoped to caller.',
          auth: 'super_admin · entity_admin · staff · ambassador',
          response: `[
  {
    "id": "412",
    "fullname": "Asha Mwangi",
    "mobile": "256708...",
    "card_no": "1234567890",
    "wallet_balance": "8200.00",
    "campus_id": "10",
    "entity_id": "19",
    "plan_type": "FEE",
    "status": "ACTIVE"
  }
]`,
          notes: ['De-duplicates on `card_no` since the upstream middleware can return ghost rows after card transfers.'],
        },
        {
          method: 'POST',
          path: '/api/subscribers',
          summary: 'Register a new subscriber.',
          auth: 'cashier · ambassador · entity_admin · super_admin',
          body: `{
  "fullname": "Asha Mwangi",
  "mobile": "256708...",
  "card_no": "1234567890",
  "campus_id": "10",
  "plan_type": "FEE",
  "initial_recharge": "5000.00"   // required when plan_type=FEE
}`,
          response: `{ "id": "412", "wallet_balance": "5000.00", ... }`,
          notes: [
            'FEE plans must include `initial_recharge >= campus.subscription_fee`. The wallet credit equals `initial_recharge − subscription_fee`.',
            'FREE plans skip the recharge and start with a 0 wallet balance.',
          ],
        },
        {
          method: 'GET',
          path: '/api/subscribers/{id}',
          summary: 'Fetch a single subscriber with full profile + wallet balance.',
          auth: 'Any user with access to the subscriber',
          response: `{
  "id": "412",
  "sub_name": "Asha Mwangi",
  "mobile": "256708...",
  "email": "asha@example.com",
  "gender": "F",
  "card_no": "1234567890",
  "entity_id": "19",
  "campus_id": "10",
  "category": "STUDENT",
  "student_id": "S12345",
  "student_course_id": "1",
  "curr_year_of_study": "2",
  "subscription_plan": "Fee",
  "id_type": "1",
  "id_number": "PASS-998877",
  "card_balance": 8200.0,
  "status": "active",
  "is_ambassador_reg": "N",
  "is_waba_reg": "N",
  "middleware_id": "412"
}`,
        },
        {
          method: 'PUT',
          path: '/api/subscribers/{id}',
          summary: 'Update subscriber profile (name, contact, category, IDs, status).',
          auth: 'entity_admin · super_admin · cashier (own campus)',
          body: `{
  "sub_name": "Asha Mwangi",
  "email": "asha@example.com",
  "mobile": "256708...",
  "mobile_2": "",
  "campus_id": "10",
  "category": "STUDENT",        // STUDENT | STAFF | FACULTY | VISITOR | OTHER (live from middleware)
  "gender": "F",
  "student_id": "S12345",
  "student_course_id": "1",
  "curr_year_of_study": "2",
  "id_type": "1",
  "id_number": "PASS-998877",
  "relationship_agent": "",
  "status": "active"            // "active" | "inactive"
}`,
          response: `{ ...full SubscriberResponse... }`,
          notes: [
            'All fields optional. Forwarded to middleware `/update_subscriber`.',
            'Audit action: SUBSCRIBER_UPDATED.',
          ],
        },
        {
          method: 'DELETE',
          path: '/api/subscribers/{id}',
          summary: 'Soft-delete a subscriber.',
          auth: 'entity_admin · super_admin',
          response: `{ "ok": true, "id": "412" }`,
          notes: ['Soft-deletes via status=INACTIVE on the middleware. Audit action: SUBSCRIBER_DELETED.'],
        },
        {
          method: 'GET',
          path: '/api/student-courses',
          summary: 'Live list of courses available for a given campus (used by the subscriber form).',
          auth: 'cashier · entity_admin · ambassador · super_admin',
          response: `[
  { "id": "1", "course_name": "Engineering", "course_code": "ENG" },
  { "id": "2", "course_name": "Law", "course_code": "LAW" }
]`,
          notes: ['Pass `?campus_id=10`. Pulls live from middleware `/get_student_courses`.'],
        },
        {
          method: 'GET',
          path: '/api/subscriber-categories',
          summary: 'Subscriber category lookup (STUDENT, STAFF, FACULTY, …).',
          auth: 'Any authenticated user',
          response: `[
  { "value": "STUDENT", "label": "Student" },
  { "value": "STAFF", "label": "Staff" }
]`,
          notes: ['Mirrors `/api/config/subscriber-categories` — kept for FE compat. Live from middleware.'],
        },
        {
          method: 'GET',
          path: '/api/subscriber-id-types',
          summary: 'ID-type lookup for KYC during subscriber registration.',
          auth: 'Any authenticated user',
          response: `[
  { "id": "1", "label": "Passport" },
  { "id": "2", "label": "National ID" }
]`,
        },
        {
          method: 'GET',
          path: '/api/subscribers/{id}/transactions',
          summary: 'Wallet transactions for a single subscriber (paginated).',
          auth: 'Any user with access to the subscriber',
          response: `{
  "rows": [
    {
      "id": "27",
      "trn_ref_no": "EC00000273",
      "trn_type": "CREDIT",
      "amount": "5000.00",
      "wallet_open_bal": "0.00",
      "wallet_close_bal": "5000.00",
      "channel": "AIRTEL_C2B",
      "payer_mobile": "256708726757",
      "trn_date": "2026-05-19 21:44:13"
    }
  ],
  "total": 27, "page": 1, "page_size": 20, "total_pages": 2
}`,
          notes: [
            '`payer_mobile` is the number that initiated the payment (mobile-money push). Useful for AIRTEL_C2B / MTN_MOMO reconciliation.',
          ],
        },
        {
          method: 'POST',
          path: '/api/subscribers/{id}/send-sms',
          summary: 'Send a one-off SMS to the subscriber.',
          auth: 'entity_admin · staff · super_admin',
          body: `{ "message": "Your wallet is low — top up at any Ecowater store." }`,
          response: `{ "status": "sent", "sms_id": "..." }`,
          notes: [
            'Persists into `db.sms_log` with status `sent` or `failed`. Failures preserve the gateway error in `failure_reason`.',
            'Triggers audit log entries `SMS_SENT` / `SMS_FAILED`.',
          ],
        },
        {
          method: 'POST',
          path: '/api/subscribers/{id}/renew-card',
          summary: 'Issue a new card for an existing subscriber.',
          auth: 'cashier · entity_admin · super_admin',
          body: `{ "new_card_no": "9876543210" }`,
          response: `{ "ok": true, "old_card_no": "1234567890", "new_card_no": "9876543210" }`,
          notes: ['Old card is automatically marked CLOSED upstream. Wallet balance carries over to the new card unchanged.'],
        },
        {
          method: 'POST',
          path: '/api/recharge-card',
          summary: 'Top up a subscriber wallet using cashier float.',
          auth: 'cashier · entity_admin · super_admin',
          body: `{
  "card_no": "1234567890",
  "amount": "10000.00"
}`,
          response: `{
  "ok": true,
  "trn_ref_no": "EC00000274",
  "new_balance": "15000.00"
}`,
          notes: [
            'Deducts the amount from `cashier.float_balance`. Fails with 400 if float is insufficient.',
            'Posts to the upstream middleware via /recharge_card.',
          ],
        },
        {
          method: 'GET',
          path: '/api/cards',
          summary: 'List cards across the caller scope (entity / campus).',
          auth: 'super_admin · entity_admin · staff',
          response: `[
  {
    "id": "301",
    "card_no": "1310008303489",
    "card_type": "STUDENT",
    "occupied": true,
    "sub_id": "412",
    "sub_name": "Asha Mwangi",
    "entity_id": "19",
    "campus_id": "10",
    "card_balance": "8200.00",
    "currency": "UGX"
  }
]`,
          notes: ['Live from middleware `/get_cards`. Supports `?entity_id` & `?campus_id` filters.'],
        },
        {
          method: 'GET',
          path: '/api/cards/verify/{card_no}',
          summary: 'Check whether a card_no exists and is currently assigned to a subscriber.',
          auth: 'cashier · ambassador · entity_admin · super_admin',
          response: `{
  "exists": true,
  "occupied": false,
  "sub_name": null,
  "sub_id": null,
  "card_balance": "0.00"
}`,
          notes: [
            'Used by the Cashier Dashboard, WABA finalize and Subscriber Create flows before assigning a card.',
            'Authoritative source is the live middleware `/get_card_details` (the local shadow collection is no longer consulted).',
          ],
        },
        {
          method: 'GET',
          path: '/api/cards/{card_id}/details',
          summary: 'Full card record + linked subscriber + recent activity.',
          auth: 'super_admin · entity_admin · cashier',
          response: `{
  "id": "301",
  "card_no": "1310008303489",
  "card_type": "STUDENT",
  "occupied": true,
  "sub_name": "Asha Mwangi",
  "sub_id": "412",
  "entity_id": "19",
  "campus_id": "10",
  "card_balance": "8200.00",
  "currency": "UGX",
  "issued_at": "2026-04-12 10:11:00",
  "last_used_at": "2026-05-19 21:44:13"
}`,
        },
        {
          method: 'GET',
          path: '/api/cashiers',
          summary: 'List cashiers (and ambassadors) visible to the caller.',
          auth: 'super_admin · entity_admin',
          response: `[
  {
    "id": "...",
    "user_id": "...",
    "user_fullname": "Mary Cashier",
    "user_mobile": "255756...",
    "cashier_type": "Campus Agent",
    "campus_id": "10",
    "campus_name": "Kampala Main",
    "entity_id": "19",
    "assigned_float": "1500000.00",
    "assigned_cards": "120",
    "send_sms": "Yes",
    "status": "active"
  }
]`,
        },
        {
          method: 'POST',
          path: '/api/cashiers',
          summary: 'Promote a user to cashier / ambassador on a specific campus.',
          auth: 'super_admin · entity_admin',
          body: `{
  "user_id": "...",
  "cashier_type": "Campus Agent",      // "Campus Agent" | "Ambassador"
  "campus_id": "10",
  "assigned_float": "0",
  "assigned_cards": "0",
  "sell_bottle": "No",
  "recharge": "Yes",
  "recharge_limit": "0",
  "offer": "No",
  "sub_free_plan": "No",
  "send_sms": "Yes"
}`,
          response: `{ ...full CashierResponse... }`,
          notes: ['Forwarded to middleware `/create_agent`. The user must already exist.'],
        },
        {
          method: 'PUT',
          path: '/api/cashiers/{id}',
          summary: 'Update a cashier — float/card adjustments are SIGNED DELTAS, not absolutes.',
          auth: 'super_admin · entity_admin',
          body: `{
  "cashier_type": "Campus Agent",
  "campus_id": "10",
  "agent": "Y",
  "float_delta": -10000,    // SIGNED — negative deducts float
  "card_delta": 5,          // SIGNED — adds 5 cards
  "sell_bottle": "Yes",
  "recharge": "Yes",
  "recharge_limit": 50000,
  "special_offer": "No",
  "sub_free_plan": "No",
  "send_sms": "Yes",
  "status": "Active"
}`,
          response: `{ ...full CashierResponse... }`,
          notes: [
            'Per the middleware `/update_agent` contract, `float_delta` and `card_delta` are deltas — pass `0` (or omit) for no change.',
            'Audit action: CASHIER_UPDATED.',
          ],
        },
        {
          method: 'DELETE',
          path: '/api/cashiers/{id}',
          summary: 'Deactivate a cashier (does not delete the underlying user).',
          auth: 'super_admin · entity_admin',
          response: `{ "ok": true, "id": "..." }`,
          notes: ['Sets status=Inactive on the middleware. The user account remains; only the cashier role is removed.'],
        },
        {
          method: 'GET',
          path: '/api/cashiers/{id}/analytics',
          summary: 'Per-cashier KPIs.',
          auth: 'super_admin · entity_admin',
          response: `{
  "total_recharges_today": "240000.00",
  "subscribers_registered_today": 4,
  "float_balance": "1500000.00",
  "recent_activity": [{ "type": "recharge", "amount": "10000", "at": "..." }]
}`,
        },
      ],
    },
    {
      heading: 'Users · Auth Management',
      body:
        'Admin operations for user accounts: create / list / update / delete, plus password rotation and account-lock recovery. Login itself lives in the Authentication section.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/users',
          summary: 'List users visible to caller. Super admin → all; entity admin → users in own entity.',
          auth: 'super_admin · entity_admin',
          response: `[
  {
    "id": "...",
    "username": "255767821881",
    "email": "jane@example.com",
    "fullname": "Jane Doe",
    "role": "entity_admin",
    "entity_id": "19",
    "mobile": "255767821881",
    "status": "active",
    "created_at": "2026-05-18T06:00:00Z",
    "last_login": "2026-05-19T18:00:00Z",
    "locked": false,
    "must_change_password": false
  }
]`,
        },
        {
          method: 'POST',
          path: '/api/users',
          summary: 'Create a new user account (and, optionally, mirror as middleware user for cashier roles).',
          auth: 'super_admin · entity_admin',
          body: `{
  "fullname": "Jane Doe",
  "email": "jane@example.com",
  "mobile": "255767821881",          // E.164 digits — also used as username
  "password": "TempPass2026",
  "role": "entity_admin",            // "super_admin" | "entity_admin" | "staff" | "cashier" | "ambassador"
  "entity_id": "19",
  "campus_id": "10",                 // required for cashier / ambassador
  "agent": "Y",                      // auto-derived from role if omitted
  "assigned_float": 0,
  "card_balance": 0,
  "send_sms": "Y"
}`,
          response: `{ ...full UserResponse... }`,
          notes: [
            'Password is stored using PBKDF2 (per the auth playbook). `must_change_password` defaults to `true` so the user is forced to rotate on first login.',
            'Mobile must be unique. Returns 409 if it already exists.',
            'Audit action: USER_CREATED.',
          ],
        },
        {
          method: 'GET',
          path: '/api/users/{user_id}',
          summary: 'Fetch a single user profile.',
          auth: 'super_admin · entity_admin (own scope) · the user themselves',
          response: `{ ...full UserResponse... }`,
        },
        {
          method: 'PUT',
          path: '/api/users/{user_id}',
          summary: 'Update profile (name, email, mobile, role, entity, status).',
          auth: 'super_admin · entity_admin',
          body: `{
  "fullname": "Jane M. Doe",
  "email": "jane.m@example.com",
  "role": "entity_admin",
  "entity_id": "19",
  "mobile": "255767821881",
  "status": "active"                  // "active" | "inactive"
}`,
          response: `{ ...full UserResponse... }`,
          notes: [
            'All fields optional — only those present are updated.',
            'Changing `role` to/from cashier roles updates the middleware agent record accordingly.',
            'Audit action: USER_UPDATED.',
          ],
        },
        {
          method: 'DELETE',
          path: '/api/users/{user_id}',
          summary: 'Soft-delete (deactivate) a user account.',
          auth: 'super_admin · entity_admin',
          response: `{ "ok": true, "id": "..." }`,
          notes: [
            'Sets status=inactive. The user can no longer log in. History (audit logs, recharges) is preserved.',
            'Cannot delete yourself or the last remaining super_admin.',
            'Audit action: USER_DELETED.',
          ],
        },
        {
          method: 'POST',
          path: '/api/auth/change-password',
          summary: 'Authenticated user changes their own password.',
          auth: 'Any authenticated user',
          body: `{
  "current_password": "OldPass2026",
  "new_password": "NewSecurePass2027"
}`,
          response: `{ "ok": true }`,
          notes: [
            'Verifies `current_password` against the stored PBKDF2 hash. Returns 400 on mismatch.',
            'New password must satisfy the platform policy: ≥ 10 chars, mix of letters + digits.',
            'On success: `must_change_password=false`, `password_changed_at` stamped, and the last N (configurable) hashes are kept to prevent reuse.',
            'Audit action: PASSWORD_CHANGED.',
          ],
        },
        {
          method: 'POST',
          path: '/api/users/{user_id}/unlock',
          summary: 'Clear a brute-force lockout on a user account.',
          auth: 'super_admin · entity_admin',
          response: `{ "ok": true, "id": "..." }`,
          notes: [
            'Resets `failed_attempts` to 0 and clears `lockout_until`. The user can immediately log in again.',
            'Use this after the user verifies their identity out-of-band.',
            'Audit action: USER_UNLOCKED.',
          ],
        },
      ],
    },
    {
      heading: 'Wallet · Float Ledger',
      body:
        'Money-flow endpoints. The Float Ledger is the source-of-truth for cash injected by Ecowater into card_float / agent_float; wallet transactions track subscriber-side movement.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/wallet-transactions',
          summary: 'Paginated wallet transactions across all subscribers visible to caller.',
          auth: 'super_admin · entity_admin · staff',
          response: `{
  "rows": [{ "id": "27", "trn_ref_no": "EC00000273", "amount": "5000.00", "payer_mobile": "256708...", "channel": "AIRTEL_C2B", ... }],
  "total": 142, "page": 1, "page_size": 20, "total_pages": 8
}`,
          notes: ['Supports ?entity_id, ?subscriber_id, ?trn_type, ?from_date, ?to_date query filters.'],
        },
        {
          method: 'GET',
          path: '/api/float-ledger',
          summary: 'List float ledger entries.',
          auth: 'super_admin',
          response: `[
  {
    "id": "fl-001",
    "entity_id": "19",
    "type": "DEPOSIT",   // DEPOSIT | WITHDRAWAL
    "target": "card_float",
    "amount": "5000000.00",
    "currency": "UGX",
    "reference": "BANK-INV-1234",
    "created_at": "2026-05-19T20:00:00Z"
  }
]`,
        },
        {
          method: 'POST',
          path: '/api/float-ledger',
          summary: 'Record a new float entry (and post upstream).',
          auth: 'super_admin',
          body: `{
  "entity_id": "19",
  "type": "DEPOSIT",
  "target": "card_float",
  "amount": "5000000.00",
  "reference": "BANK-INV-1234"
}`,
          notes: [
            'Mirrors the entry into the upstream middleware GL.',
            'Effect on balances:  DEPOSIT increases the target float;  WITHDRAWAL decreases it. Underflows are blocked at the middleware boundary, not locally.',
          ],
        },
        {
          method: 'GET',
          path: '/api/float-ledger/balances',
          summary: 'Aggregated card_float / agent_float balances per entity.',
          auth: 'super_admin',
          response: `[
  { "entity_id": "19", "card_float": "120000.00", "agent_float": "300000.00" }
]`,
          notes: ['Computed locally as `sum(DEPOSIT) − sum(WITHDRAWAL)` partitioned by entity_id + target.'],
        },
        {
          method: 'GET',
          path: '/api/float-ledger/middleware-balances',
          summary: 'Live GL balances pulled directly from middleware /get_gl_balances.',
          auth: 'super_admin',
          notes: ['Used as the authoritative reconciliation source against /api/float-ledger/balances.'],
        },
      ],
    },
    {
      heading: 'Messaging · Send SMS',
      body:
        'Broadcast and one-off SMS to subscribers. All sends fan out to the upstream SMS gateway POST https://ecobridge.tech/send_sms with payload { entity_id, phone, message, card_number }. Sends honour the global middleware-writes kill-switch — when disabled, each recipient is logged with status="blocked" so flows can be rehearsed safely.',
      endpoints: [
        {
          method: 'POST',
          path: '/api/messaging/preview',
          summary: 'Resolve the recipient list for a set of filters without sending.',
          auth: 'entity_admin · staff · super_admin',
          body: `{
  "entity_id": "19",             // optional for super_admin; pinned for entity roles
  "categories": ["STUDENT"],      // inclusive OR; empty = all
  "campus_ids": ["10"],
  "statuses": ["active"]
}`,
          response: `{
  "recipients_total": 240,
  "recipients_deliverable": 232,
  "recipients_skipped_no_mobile": 8,
  "sample": [ { "sub_id": "412", "sub_name": "Asha Mwangi", "mobile": "256708...", "has_mobile": true } ]
}`,
          notes: ['`sample` is capped at 200 rows to keep the payload small.'],
        },
        {
          method: 'POST',
          path: '/api/messaging/send',
          summary: 'Send SMS broadcast — fan-out to every subscriber matching the filters.',
          auth: 'entity_admin · staff · super_admin',
          body: `{
  "entity_id": "19",
  "categories": [], "campus_ids": [], "statuses": [],
  "message": "Ecowater: stores close early on Friday."
}`,
          response: `{
  "broadcast_id": "BC-20260720093000-a1b2c3",
  "sent": 230, "failed": 2, "blocked": 0,
  "failures": [ { "mobile": "2567...", "error": "SMS gateway rejected" } ]
}`,
          notes: [
            'Calls the gateway once per recipient (modest concurrency to avoid rate limits).',
            'Message max length: 1000 chars. Returns 400 when no recipients match the filters.',
            'Every recipient is persisted in `db.subscriber_messages` plus a roll-up row in `db.messaging_broadcasts`.',
            'When middleware writes are disabled, recipients are logged with status="blocked" (nothing leaves the platform).',
          ],
        },
        {
          method: 'GET',
          path: '/api/messaging/broadcasts',
          summary: 'Recent broadcast history for the caller.',
          auth: 'entity_admin · staff · super_admin',
          response: `{
  "rows": [
    {
      "broadcast_id": "BC-20260720093000-a1b2c3",
      "message": "Ecowater: stores close early on Friday.",
      "sent": 230, "failed": 2, "blocked": 0,
      "sender_name": "Jane Doe", "created_at": "2026-07-20T09:30:00Z"
    }
  ]
}`,
          notes: ['`?limit=25` (max 200). Non-super-admins only see their own broadcasts.'],
        },
        {
          method: 'POST',
          path: '/api/subscribers/{id}/send-sms',
          summary: 'Send a one-off SMS to a single subscriber.',
          auth: 'entity_admin · staff · super_admin',
          body: `{ "message": "Your wallet is low — top up at any Ecowater store." }`,
          response: `{ "status": "sent", "sms_id": "..." }`,
          notes: [
            'Uses the same gateway call as the broadcast endpoint.',
            'Persists into `db.sms_log`; failures preserve the gateway error in `failure_reason`.',
            'Audit actions: `SMS_SENT` / `SMS_FAILED`.',
          ],
        },
      ],
    },
    {
      heading: 'Utility Bills · Invoices',
      body:
        'Meter-reading-based utility billing. Each bill records a single store/utility/month reading. An invoice rolls up all bills for a given campus + billing month into one document that the campus issues to Ecowater.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/campuses/{id}/utility-bills',
          summary: 'List bills for a campus (newest first).',
          auth: 'super_admin · entity_admin',
        },
        {
          method: 'POST',
          path: '/api/campuses/{id}/utility-bills',
          summary: 'Log a new meter reading. Server auto-computes usage and total.',
          auth: 'super_admin · entity_admin',
          body: `{
  "bill_type": "water",          // "water" | "electricity"
  "store_id": "5",
  "meter_reading": 1250,         // current reading
  "billing_month": "2026-06"     // YYYY-MM
}`,
          response: `{
  "id": "...",
  "bill_type": "water",
  "store_id": "5",
  "store_name": "Kampala HQ Pump",
  "billing_month": "2026-06",
  "prev_reading": 1000,
  "meter_reading": 1250,
  "usage": 250,
  "rate": 1300,
  "amount": 325000
}`,
          notes: [
            'Computation: `prev_reading` is the most recent reading for the same `store_id + bill_type` (any month). For the very first bill, `prev_reading` defaults to 0.',
            '`usage = max(meter_reading − prev_reading, 0)` — negative readings are clamped to 0 to defend against meter swaps.',
            '`rate = store.utility_water` or `store.utility_electricity` depending on bill_type.',
            '`amount = usage × rate`, rounded to 2 decimals.',
          ],
        },
        {
          method: 'DELETE',
          path: '/api/campuses/{id}/utility-bills/{bill_id}',
          summary: 'Remove a bill entry.',
          auth: 'super_admin · entity_admin',
        },
        {
          method: 'GET',
          path: '/api/campuses/{id}/invoices',
          summary: 'List invoices for the campus, newest first.',
          auth: 'super_admin · entity_admin',
        },
        {
          method: 'POST',
          path: '/api/campuses/{id}/invoices',
          summary: 'Generate a consolidated invoice from all bills in the selected month.',
          auth: 'super_admin · entity_admin',
          body: `{
  "billing_month": "2026-05",
  "notes": "Q2 settlement, payable in 30 days"
}`,
          response: `{
  "id": "...",
  "invoice_no": "INV-10-202605-01",
  "billing_month": "2026-05",
  "campus_name": "Kampala Main",
  "entity_name": "REFILL ECOWATER LIMITED",
  "line_items": [
    { "store_name": "Kampala HQ Pump", "bill_type": "water", "prev_reading": 0, "meter_reading": 1000, "usage": 1000, "rate": 1300, "amount": 1300000 }
  ],
  "subtotal_water": 1300000,
  "subtotal_electricity": 177500,
  "total": 1477500,
  "status": "issued",
  "currency": "UGX"
}`,
          notes: [
            'Aggregation: every bill matching `campus_id + billing_month` becomes a line item.',
            'Invoice numbering: `INV-{campus_id}-{YYYYMM}-{seq:02d}`. The sequence resets per (campus, billing_month) tuple.',
            'Returns 400 with "No bills found" if there are no bills in the selected month.',
          ],
        },
        {
          method: 'PATCH',
          path: '/api/campuses/{id}/invoices/{invoice_id}',
          summary: 'Update the lifecycle status of an invoice.',
          auth: 'super_admin · entity_admin',
          body: `{
  "status": "paid",          // "issued" | "sent" | "paid"
  "paid_on": "2026-05-30"    // required when status = "paid", YYYY-MM-DD
}`,
          notes: [
            'Transitions: any → any. The intended walk is issued → sent → paid.',
            'When status becomes "sent" the first time, the server stamps `sent_at` (UTC ISO).',
            'When status becomes "paid", `paid_on` (user-entered) and `paid_at` (server-recorded) are stored.',
            '`paid_on` is required for status=paid; missing it yields 400.',
          ],
        },
        {
          method: 'GET',
          path: '/api/campuses/{id}/invoices/{invoice_id}/pdf',
          summary: 'Stream the invoice as a PDF (reportlab, navy + gold styling).',
          auth: 'super_admin · entity_admin',
          notes: [
            'Content-Disposition: attachment, filename = `<invoice_no>.pdf`.',
            'PDF header shows live status (color-coded) and the Paid On date when applicable.',
          ],
        },
      ],
    },
    {
      heading: 'Dashboards · Analytics · Audit',
      body: 'Reporting and observability endpoints. All dashboards rely on the in-process TTL cache + parallel middleware fan-out.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/dashboard/stats',
          summary: 'Tile data for the entity-admin dashboard.',
          auth: 'entity_admin · staff',
          response: `{
  "subscribers_total": 412,
  "stores_total": 3,
  "wallet_balance_total": "1240500.00",
  "revenue_30d": "8430000.00",
  "trend": [{ "date": "2026-05-01", "amount": "210000.00" }]
}`,
        },
        {
          method: 'GET',
          path: '/api/super-dashboard/overview',
          summary: 'Cross-entity rollup for super admin.',
          auth: 'super_admin',
          response: `{
  "entities_total": 2,
  "campuses_total": 3,
  "stores_total": 4,
  "subscribers_total": 4,
  "users_total": 2,
  "by_country": [{ "country": "UG", "stores": 3, "subscribers": 3 }],
  "top_entities": [{ "entity_name": "...", "stores": 3, "share_pct": 75.0 }]
}`,
        },
        {
          method: 'GET',
          path: '/api/entity-dashboard/overview',
          summary: 'Live entity dashboard data — KPI tiles + revenue trend + store leaderboard.',
          auth: 'entity_admin · staff',
        },
        {
          method: 'GET',
          path: '/api/analytics/utilities',
          summary: 'Utility bill analytics for the caller scope (entity or super-admin-wide).',
          auth: 'super_admin · entity_admin',
          notes: [
            'Aggregates db.utility_bills by `bill_type` and `billing_month`. Used by the Utility Bills analytics tab.',
          ],
        },
        {
          method: 'GET',
          path: '/api/audit-logs',
          summary: 'Audit log feed — every admin write action.',
          auth: 'super_admin',
          response: `[
  {
    "id": "...",
    "actor_id": "...",
    "actor_name": "Jane Doe",
    "action": "INVOICE_GENERATED",
    "target": "INV-10-202605-01",
    "entity_id": "19",
    "created_at": "2026-05-19T21:44:13Z"
  }
]`,
          notes: [
            'Actions logged: ENTITY_CREATED, ENTITY_UPDATED, CAMPUS_CREATED, USER_CREATED, FLOAT_DEPOSIT, FLOAT_WITHDRAWAL, INVOICE_GENERATED, INVOICE_STATUS_CHANGED, SMS_SENT, SMS_FAILED — among others.',
          ],
        },
      ],
    },
    {
      heading: 'Documentation',
      body: 'Documentation self-service. The frontend renders the same content shown in this page; the PDF export uses reportlab.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/docs/api/pdf',
          summary: 'Download the API documentation as a PDF.',
          auth: 'Any authenticated user',
          notes: ['Mirrors this very page. Content is sourced from /app/backend/routers/docs.py.'],
        },
      ],
    },
  ],
};
